import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import WaitingListComponent from '../../../Components/AdminComponents/WaitingList';
import {showOverCapacityWarningModal} from '../../../Components/AdminComponents/EventRegistration/showOverCapacityWarningModal';
import {setPageNumber, setPageSize} from '../../../Store/Actions/pageSize';
import {queryString} from '../../../Util/commonUtility';
import {
  getRegistrationWaitingListById,
  getRegistrationWaitingListByIdForCSV,
  moveWaitingListToRegistration,
  cancelWaitingListEntry,
} from '../../../Store/Actions/eventRegister';
import {useParams} from 'react-router-dom';

const WaitingListContainer = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const {
    allRegistrationWaitingListByEvent,
    allRegistrationWaitingListByEventForCSV,
  } = useSelector(({eventRegister}) => eventRegister);
  const memorizedPageNumber = useSelector(
    ({pageSizes}) => pageSizes.pageNumbers.waitingList,
  );
  const memorizedPageSize = useSelector(
    ({pageSizes}) => pageSizes.pageSizes.waitingList,
  );

  const [allRegistrationWaitingList, setAllRegistrationWaitingList] =
    useState();
  const [paginationAndSortChanged, setPaginationAndSortChanged] =
    useState(false);
  const [
    allRegistrationWaitingListForCSV,
    setAllRegistrationWaitingListForCSV,
  ] = useState();

  const [csvDataLoading, setCSVDataLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: memorizedPageNumber || 1,
    pageCount: memorizedPageSize || 10,
    totalPages: 1,
    totalCount: 0,
  });
  const [sortParams, setSortParams] = useState({
    field: 'id',
    order: 'descend',
  });
  const [movingWaitingListId, setMovingWaitingListId] = useState(null);
  const [cancellingWaitingListId, setCancellingWaitingListId] = useState(null);
  const searchValues = useSelector(
    ({dynamicSearch}) => dynamicSearch.searchValues.waitingList,
  );

  useEffect(() => {
    fetchWaitingList(searchValues);
  }, []);

  useEffect(() => {
    if (allRegistrationWaitingListByEvent) {
      setAllRegistrationWaitingList(
        allRegistrationWaitingListByEvent?.waitingList,
      );
      setPagination({
        ...pagination,
        page: allRegistrationWaitingListByEvent?.currentPage,
        totalPages: allRegistrationWaitingListByEvent?.totalPages,
        totalCount: allRegistrationWaitingListByEvent?.totalCount,
      });
    }
  }, [allRegistrationWaitingListByEvent]);

  useEffect(() => {
    if (allRegistrationWaitingListByEventForCSV) {
      setAllRegistrationWaitingListForCSV(
        allRegistrationWaitingListByEventForCSV?.waitingList,
      );
    }
  }, [allRegistrationWaitingListByEventForCSV]);

  useEffect(() => {
    if (paginationAndSortChanged) {
      fetchWaitingList(searchValues);
      setPaginationAndSortChanged(false);
    }
  }, [paginationAndSortChanged, searchValues]);

  const handlePaginationChange = (current, pageSize) => {
    dispatch(setPageSize('waitingList', pageSize));
    dispatch(setPageNumber('waitingList', current));
    setPagination({
      ...pagination,
      page: current,
      pageCount: pageSize,
    });
    setPaginationAndSortChanged(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // AntD onChange also runs for pagination; keep existing sort when sorter is empty.
    if (sorter?.field && sorter?.order) {
      setSortParams({
        field: sorter.field,
        order: sorter.order,
      });
    }
    setPaginationAndSortChanged(true);
  };

  const fetchWaitingList = async (values, paginationOverride) => {
    try {
      const sortField = sortParams.field || '';
      const sortOrder = sortParams.order || '';
      const searchQueryData = queryString(values);
      dispatch(
        getRegistrationWaitingListById(
          paginationOverride || pagination,
          sortField,
          sortOrder,
          searchQueryData,
          id,
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWaitingListForCSV = async (values) => {
    const sortField = sortParams.field || '';
    const sortOrder = sortParams.order || '';
    const searchQueryData = queryString(values);

    dispatch(
      getRegistrationWaitingListByIdForCSV(
        sortField,
        sortOrder,
        searchQueryData,
        pagination.totalCount,
        id,
        setCSVDataLoading,
      ),
    );
  };

  const handleMoveToRegistration = async (
    waitingListId,
    confirmOverCapacity = false,
  ) => {
    setMovingWaitingListId(waitingListId);
    const result = await dispatch(
      moveWaitingListToRegistration(
        waitingListId,
        () => {
          setMovingWaitingListId(null);
          fetchWaitingList(searchValues);
        },
        () => setMovingWaitingListId(null),
        confirmOverCapacity,
      ),
    );
    if (result?.requiresOverCapacityConfirmation && !confirmOverCapacity) {
      await showOverCapacityWarningModal({
        fallbackMessage: result.errorMessage,
        onConfirm: () => handleMoveToRegistration(waitingListId, true),
      });
    }
  };

  const handleCancelWaitingListEntry = (waitingListId) => {
    setCancellingWaitingListId(waitingListId);
    dispatch(
      cancelWaitingListEntry(
        waitingListId,
        () => {
          setCancellingWaitingListId(null);
          fetchWaitingList(searchValues);
        },
        () => setCancellingWaitingListId(null),
      ),
    );
  };

  return (
    <div>
      <WaitingListComponent
        waitingListData={allRegistrationWaitingList || []}
        waitingListDataForCSV={allRegistrationWaitingListForCSV || []}
        csvDataLoading={csvDataLoading}
        fetchWaitingListForCSV={fetchWaitingListForCSV}
        searchValues={searchValues}
        onSearch={(values) => {
          dispatch(setPageNumber('waitingList', 1));
          const updatedPagination = {
            ...pagination,
            page: 1,
          };
          setPagination({
            ...updatedPagination,
          });
          fetchWaitingList(values, updatedPagination);
        }}
        paging={pagination}
        handlePaginationChange={(page, pageSize) => {
          handlePaginationChange(page, pageSize);
        }}
        handleTableChange={(pagination, filter, sorter) => {
          handleTableChange(pagination, filter, sorter);
        }}
        sortParams={sortParams}
        onMoveToRegistration={handleMoveToRegistration}
        movingWaitingListId={movingWaitingListId}
        onCancelWaitingListEntry={handleCancelWaitingListEntry}
        cancellingWaitingListId={cancellingWaitingListId}
      />
    </div>
  );
};

export default WaitingListContainer;
